import React, { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { useScript } from 'hoofd'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocStep } from '../shared/DocStep'

const Creation = () => {
  const { t } = useLingui()

  const howToData = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Create a Guild',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Open the Command Palette',
            text: 'Click the Command Palette icon in the top navigation bar, or use the keyboard shortcut to open the Command Palette.',
          },
          {
            '@type': 'HowToStep',
            name: 'Select "Create a Guild"',
            text: 'Click the "Create a Guild" option from the quick actions, or type "create guild" in the search field.',
          },
          {
            '@type': 'HowToStep',
            name: 'Fill in your Guild details',
            text: 'Enter a name and description for your Guild.',
          },
          {
            '@type': 'HowToStep',
            name: 'Customize your Guild URL',
            text: 'Choose a short name (slug) that becomes your Guild URL, such as /metro-coders.',
          },
          {
            '@type': 'HowToStep',
            name: 'Add photos',
            text: 'Upload a profile photo and cover photo for your Guild.',
          },
          {
            '@type': 'HowToStep',
            name: 'Save your Guild',
            text: "Click Save to create your Guild. You will be redirected to your new Guild's profile page.",
          },
        ],
      }),
    []
  )
  useScript({ type: 'application/ld+json', text: howToData })

  return (
    <>
      <Card gap='$4'>
        <DocSection id='creating-a-guild' title={t`Creating a Guild`}>
          <Paragraph size='$5'>
            <Trans>
              Guilds are created through the Command Palette — a search and
              action interface built into the top navigation bar. Setting up a
              new Guild takes just a few minutes. You&apos;ll need to be signed
              in to create one.
            </Trans>
          </Paragraph>
          <DocStep
            number={1}
            title={t`Open the Command Palette`}
            description={t`Click the Command Palette icon in the top navigation bar, or press the keyboard shortcut. A search input will appear with quick action options below it.`}
            screenshot={{
              src: 'docs/getting-started/command-menu-open.png',
              alt: t`Command Palette dropdown showing search input with quick action options: Create an Event, Create a Presentation, Create a Guild, plus contextual suggestions`,
              caption: t`The Command Palette with quick actions and contextual suggestions`,
            }}
          />
          <DocStep
            number={2}
            title={t`Select "Create a Guild"`}
            description={t`Click the "Create a Guild" option from the quick actions list. The Command Palette supports natural language, so you can also type "create guild" or phrases like "start a new community" in the search field.`}
          />
          <DocStep
            number={3}
            title={t`Fill in your Guild details`}
            description={t`Enter a name for your Guild and write a short description that tells people what your community is about. A good description helps visitors understand your community's focus and decide whether to join.`}
          />
          <DocStep
            number={4}
            title={t`Customize your Guild URL`}
            description={t`Choose a short name (slug) for your Guild. This becomes part of your Guild's permanent URL — for example, a Guild with the short name "metro-coders" is available at guild.host/metro-coders. Pick something short, memorable, and relevant to your community.`}
          />
          <DocStep
            number={5}
            title={t`Add photos`}
            description={t`Upload a profile photo and a cover photo to give your Guild a recognizable identity. The profile photo appears in search results and Member lists, while the cover photo is displayed prominently at the top of your Guild's profile page.`}
          />
          <DocStep
            number={6}
            title={t`Save your Guild`}
            description={t`Click Save to create your Guild. You'll be redirected to your new Guild's profile page, where you can continue setting things up.`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='after-creation' title={t`What to Do Next`}>
          <Paragraph size='$5'>
            <Trans>
              Once your Guild is created, here are some recommended next steps
              to get your community up and running:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='settings'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Configure your settings
                  </Paragraph>{' '}
                  — Visit{' '}
                  <Link href='/docs/guilds/settings'>Guild Settings</Link> to
                  set your timezone, fine-tune your description, and customize
                  your Guild.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='calendar-add'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Create your first Event
                  </Paragraph>{' '}
                  — Use the Command Palette again to{' '}
                  <Link href='/docs/events/creation'>create an Event</Link> for
                  your Guild. Events are the primary way communities come
                  together.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='person-add'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Invite Members
                  </Paragraph>{' '}
                  — Share your Guild&apos;s URL with your community. Anyone can
                  join a public Guild with a single click. If you&apos;d like to
                  gate membership, set up a{' '}
                  <Link href='/docs/guilds/forms'>Membership Form</Link>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='globe'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Connect Bluesky
                  </Paragraph>{' '}
                  — Set up the{' '}
                  <Link href='/docs/guilds/bluesky'>Bluesky integration</Link>{' '}
                  to automatically announce your Events on the decentralized
                  social network.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>
    </>
  )
}

export default Creation
